/**
 * ManualProductGrouping.tsx
 * Admin UI for manually creating product groups to share reviews
 * Fetches products from Shopify in real-time (not from reviews)
 * Path: components/admin-tabs/reviews/ManualProductGrouping.tsx
 */

import React, { useState, useEffect } from 'react';
import { api } from '../../../services/api';
import { UserProfile } from '../../../types';
import { Search, Plus, Trash2, Edit2, X, Check, Package, RefreshCw } from 'lucide-react';

interface ProductGroup {
  id: string;
  admin_id: string;
  group_name: string;
  product_ids: string[];
  created_at: string;
}

interface ShopifyProduct {
  id: string;
  product_id: string; // Shopify product handle
  title: string;
  image_url?: string;
  variants_count: number;
}

interface ManualProductGroupingProps {
  user: UserProfile;
}

export const ManualProductGrouping: React.FC<ManualProductGroupingProps> = ({ user }) => {
  const [groups, setGroups] = useState<ProductGroup[]>([]);
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<ShopifyProduct[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  
  // Create group form
  const [groupName, setGroupName] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    loadGroups();
    loadShopifyProducts();
  }, [user.id]);

  useEffect(() => {
    filterProducts();
  }, [searchQuery, products]);

  // Load existing groups
  const loadGroups = async () => {
    try {
      const data = await api.getProductGroups(user.id);
      setGroups(data || []);
    } catch (error) {
      console.error('Failed to load groups:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load products from Shopify (real-time)
  const loadShopifyProducts = async () => {
    setLoadingProducts(true);
    try {
      // Call API to fetch from Shopify
      const shopifyProducts = await api.fetchShopifyProducts(user.id);
      
      // Transform to our format
      const formattedProducts: ShopifyProduct[] = shopifyProducts.map((p: any) => ({
        id: p.id,
        product_id: p.handle, // Use handle as product_id
        title: p.title,
        image_url: p.image?.src || p.images?.[0]?.src,
        variants_count: p.variants?.length || 0
      }));
      
      setProducts(formattedProducts);
      setFilteredProducts(formattedProducts);
      
      console.log(`✅ Loaded ${formattedProducts.length} products from Shopify`);
    } catch (error) {
      console.error('Failed to load Shopify products:', error);
      alert('Failed to load products from Shopify. Check console for details.');
    } finally {
      setLoadingProducts(false);
    }
  };

  // Refresh products from Shopify
  const refreshProducts = () => {
    loadShopifyProducts();
  };

  // Filter products based on search
  const filterProducts = () => {
    if (!searchQuery.trim()) {
      setFilteredProducts(products);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = products.filter(p => 
      p.title.toLowerCase().includes(query) ||
      p.product_id.toLowerCase().includes(query)
    );
    
    setFilteredProducts(filtered);
  };

  // Toggle product selection
  const toggleProduct = (productId: string) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
  };

  // Select all filtered products
  const selectAll = () => {
    const allIds = new Set(filteredProducts.map(p => p.product_id));
    setSelectedProducts(allIds);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedProducts(new Set());
  };

  // Create new group
  const handleCreateGroup = async () => {
    if (!groupName.trim()) {
      alert('Please enter a group name');
      return;
    }

    if (selectedProducts.size < 2) {
      alert('Please select at least 2 products');
      return;
    }

    setCreating(true);
    try {
      await api.createProductGroup(user.id, groupName, Array.from(selectedProducts));
      
      // Reset form
      setGroupName('');
      setSelectedProducts(new Set());
      setShowCreateForm(false);
      
      // Reload groups
      await loadGroups();
      
      alert(`Group "${groupName}" created successfully!`);
    } catch (error) {
      console.error('Failed to create group:', error);
      alert('Failed to create group');
    } finally {
      setCreating(false);
    }
  };

  // Delete group
  const handleDeleteGroup = async (groupId: string, groupName: string) => {
    if (!confirm(`Are you sure you want to delete group "${groupName}"?`)) {
      return;
    }

    try {
      await api.deleteProductGroup(groupId);
      await loadGroups();
      alert('Group deleted successfully!');
    } catch (error) {
      console.error('Failed to delete group:', error);
      alert('Failed to delete group');
    }
  };

  // Edit group - load products into selection
  const startEditGroup = (group: ProductGroup) => {
    setEditingGroupId(group.id);
    setGroupName(group.group_name);
    setSelectedProducts(new Set(group.product_ids));
    setShowCreateForm(true);
  };

  // Update existing group
  const handleUpdateGroup = async () => {
    if (!editingGroupId) return;

    if (!groupName.trim()) {
      alert('Please enter a group name');
      return;
    }

    if (selectedProducts.size < 2) {
      alert('Please select at least 2 products');
      return;
    }

    setCreating(true);
    try {
      await api.updateProductGroup(editingGroupId, groupName, Array.from(selectedProducts));
      
      // Reset form
      setGroupName('');
      setSelectedProducts(new Set());
      setShowCreateForm(false);
      setEditingGroupId(null);
      
      // Reload groups
      await loadGroups();
      
      alert('Group updated successfully!');
    } catch (error) {
      console.error('Failed to update group:', error);
      alert('Failed to update group');
    } finally {
      setCreating(false);
    }
  };

  // Cancel edit
  const cancelEdit = () => {
    setEditingGroupId(null);
    setGroupName('');
    setSelectedProducts(new Set());
    setShowCreateForm(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Product Grouping</h2>
          <p className="text-sm text-gray-500 mt-1">
            Group products from Shopify to share reviews between them
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={refreshProducts}
            disabled={loadingProducts}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            title="Refresh products from Shopify"
          >
            <RefreshCw className={`w-4 h-4 ${loadingProducts ? 'animate-spin' : ''}`} />
            {loadingProducts ? 'Loading...' : 'Refresh'}
          </button>
          {!showCreateForm && (
            <button
              onClick={() => setShowCreateForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create Group
            </button>
          )}
        </div>
      </div>

      {/* Product Count Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Package className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-900">
              {products.length} products loaded from Shopify
            </p>
            <p className="text-xs text-blue-700 mt-1">
              Products are fetched in real-time from your Shopify store. Click "Refresh" to update the list.
            </p>
          </div>
        </div>
      </div>

      {/* Create/Edit Group Form */}
      {showCreateForm && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">
              {editingGroupId ? 'Edit Group' : 'Create New Group'}
            </h3>
            <button
              onClick={cancelEdit}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Group Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Group Name
            </label>
            <input
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="e.g., Maternity Kurtis, Summer Dresses"
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>

          {/* Search Products */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Products from Shopify ({selectedProducts.size} selected)
            </label>
            
            {/* Search Bar */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            {/* Select Actions */}
            <div className="flex gap-2 mb-3">
              <button
                onClick={selectAll}
                className="px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
              >
                Select All ({filteredProducts.length})
              </button>
              <button
                onClick={clearSelection}
                className="px-3 py-1.5 text-sm bg-gray-50 text-gray-600 rounded hover:bg-gray-100"
              >
                Clear Selection
              </button>
            </div>

            {/* Product List */}
            <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
              {loadingProducts ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-3">Loading products from Shopify...</p>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p>No products found</p>
                  {searchQuery && (
                    <p className="text-sm mt-2">Try a different search term</p>
                  )}
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredProducts.map((product) => (
                    <label
                      key={product.product_id}
                      className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedProducts.has(product.product_id) ? 'bg-blue-50' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedProducts.has(product.product_id)}
                        onChange={() => toggleProduct(product.product_id)}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      {product.image_url && (
                        <img 
                          src={product.image_url} 
                          alt={product.title}
                          className="w-10 h-10 object-cover rounded"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {product.title}
                        </p>
                        <p className="text-xs text-gray-500">
                          {product.variants_count} variant{product.variants_count !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={editingGroupId ? handleUpdateGroup : handleCreateGroup}
              disabled={creating || !groupName.trim() || selectedProducts.size < 2}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Check className="w-4 h-4" />
              {creating ? 'Saving...' : editingGroupId ? 'Update Group' : 'Create Group'}
            </button>
            <button
              onClick={cancelEdit}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Existing Groups */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Existing Groups ({groups.length})
          </h3>
        </div>

        {groups.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <Package className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <p className="text-lg font-medium">No groups created yet</p>
            <p className="text-sm mt-2">Create your first group to start sharing reviews</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {groups.map((group) => (
              <div key={group.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className="text-base font-semibold text-gray-900 mb-2">
                      {group.group_name}
                    </h4>
                    <p className="text-sm text-gray-600 mb-3">
                      {group.product_ids.length} products in this group
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {group.product_ids.slice(0, 5).map((productId) => {
                        const product = products.find(p => p.product_id === productId);
                        return (
                          <span
                            key={productId}
                            className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                          >
                            {product ? product.title : productId}
                          </span>
                        );
                      })}
                      {group.product_ids.length > 5 && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded">
                          +{group.product_ids.length - 5} more
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => startEditGroup(group)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="Edit group"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteGroup(group.id, group.group_name)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Delete group"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
